// Zvuková vrstva pexesa: klávesnice na hrací ploše, zvuky přes Web Audio, hlášení přes aria-live.
// Rychlé věci (pohyb, otočení, shoda) jsou vždy slyšet jako zvuk; text je doplněk pro čtečku.
import type { GameEvent } from "../../../games/types.ts";
import type { PexesoView } from "../../../games/pexeso/meta.ts";
import { announce, say } from "../../announce.ts";
import { play, queue } from "../../audio.ts";
import { animalName, cardText, orderText, panFor, playerName, posText, remainingPairs, resultText, rowCol, scoreText, turnText, type Model } from "./model.ts";
import "./sounds.ts";

export const HELP_TEXT =
  "Nápověda. Šipky: pohyb po kartách. Home a End: začátek a konec řádku. " +
  "Enter nebo mezerník: otočit kartu. R: stav hry, kdo je na tahu, skóre a otočené karty. T: pořadí hráčů. H: tato nápověda. " +
  "Zvuk karty uslyšíš vlevo nebo vpravo podle sloupce. " +
  "Tabulátor opustí hrací plochu. Pokud klávesy nefungují, zapni režim aplikace klávesou NVDA plus mezerník.";

export class SoundLayer {
  private onKey = (e: KeyboardEvent) => this.handleKey(e);

  private model: Model;
  private board: HTMLElement;
  /** Překreslení kurzoru ve vizuální vrstvě. */
  private onCursor: () => void;

  constructor(model: Model, board: HTMLElement, onCursor: () => void) {
    this.model = model;
    this.board = board;
    this.onCursor = onCursor;
    board.addEventListener("keydown", this.onKey);
  }

  destroy() {
    this.board.removeEventListener("keydown", this.onKey);
  }

  private get view(): PexesoView | null {
    return this.model.view;
  }

  private handleKey(e: KeyboardEvent) {
    const view = this.view;
    if (!view || e.ctrlKey || e.altKey || e.metaKey) return;
    const { row, col } = rowCol(view, this.model.cursor);
    let handled = true;
    switch (e.key) {
      case "ArrowUp":
        this.moveTo(row - 1, col);
        break;
      case "ArrowDown":
        this.moveTo(row + 1, col);
        break;
      case "ArrowLeft":
        this.moveTo(row, col - 1);
        break;
      case "ArrowRight":
        this.moveTo(row, col + 1);
        break;
      case "Home":
        this.moveTo(row, 0);
        break;
      case "End":
        this.moveTo(row, view.cols - 1);
        break;
      case "Enter":
      case " ":
        this.flip();
        break;
      default:
        if (e.key.toLowerCase() === "r") say(this.statusText());
        else if (e.key.toLowerCase() === "t") say(orderText(view, this.model.me));
        else if (e.key.toLowerCase() === "h") say(HELP_TEXT);
        else handled = false;
    }
    if (handled) e.preventDefault();
  }

  private moveTo(row: number, col: number) {
    const view = this.view!;
    if (row < 0 || col < 0 || row >= view.rows || col >= view.cols) {
      play("okraj", { pan: panFor(view, this.model.cursor) });
      return;
    }
    const index = row * view.cols + col;
    this.model.cursor = index;
    this.onCursor();
    play("kurzor", { pan: panFor(view, index) });
    say(`${posText(view, index)}, ${cardText(view.cards[index])}`);
  }

  private flip() {
    const view = this.view!;
    const i = this.model.cursor;
    if (view.over) return say(resultText(view, this.model.me));
    if (view.turn !== this.model.me) {
      play("okraj");
      return say(turnText(view, this.model.me));
    }
    if (view.pendingHide) {
      play("okraj");
      return say("Počkej, karty se zakrývají.");
    }
    if (view.cards[i].state !== "down") {
      play("okraj");
      return say(`Tahle karta je ${cardText(view.cards[i])}.`);
    }
    this.model.sendAction({ type: "flip", index: i });
  }

  rejected() {
    play("okraj");
  }

  statusText(): string {
    const view = this.view;
    if (!view) return "";
    if (view.over) return resultText(view, this.model.me);
    const parts = [turnText(view, this.model.me), scoreText(view)];
    const up = view.faceUp.map((i) => `${posText(view, i)}: ${animalName((view.cards[i] as { animal?: string }).animal)}`);
    parts.push(up.length ? `Otočené karty: ${up.join("; ")}.` : "Žádná karta není otočená.");
    parts.push(`Zbývá ${count(remainingPairs(view), "pár", "páry", "párů")}.`);
    parts.push(`Kurzor: ${posText(view, this.model.cursor)}.`);
    return parts.join(" ");
  }

  /** Události přicházejí po aplikaci na model, view je tedy už nový stav. */
  onEvents(events: GameEvent[]) {
    const view = this.view!;
    const me = this.model.me;
    for (const e of events) {
      switch (e.type) {
        case "started":
          queue(view.turn === me ? "tvuj-tah" : "souper-tah");
          announce(`Hra začala: ${count(view.rows, "řádek", "řádky", "řádků")}, ${count(view.cols, "sloupec", "sloupce", "sloupců")}, ${count(view.cards.length / 2, "pár", "páry", "párů")}. ${turnText(view, me)} Nápověda klávesou H.`);
          break;
        case "flipped": {
          const index = e.index as number;
          const pan = panFor(view, index);
          queue("otoceni", { pan });
          queue(String(e.animal), { pan });
          const name = animalName(e.animal as string);
          if (e.by === me) announce(name);
          else announce(`${playerName(view, e.by as string, me)}: ${posText(view, index)}, ${name}.`);
          break;
        }
        case "matched":
          queue("shoda");
          announce(`Shoda, ${animalName(e.animal as string)}! ${e.by === me ? "Bod pro tebe." : `Bod: ${playerName(view, e.by as string, me)}.`}`);
          break;
        case "mismatched":
          queue("neshoda");
          announce("Neshoda.");
          break;
        case "turn":
          // Po shodě hraje tentýž hráč dál; zvuk shody stačí.
          if (!e.again) queue(e.player === me ? "tvuj-tah" : "souper-tah");
          if (e.again) announce(e.player === me ? "Hraješ znovu." : `${playerName(view, e.player as string, me)} hraje znovu.`);
          else announce(turnText(view, me));
          break;
        case "gameOver": {
          const won = (e.winners as string[]).includes(me);
          queue(won ? "vyhra" : "prohra");
          announce(resultText(view, me));
          break;
        }
      }
    }
  }
}

function count(n: number, one: string, few: string, many: string): string {
  return `${n} ${n === 1 ? one : n >= 2 && n <= 4 ? few : many}`;
}
